import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import PageContainer from '../../components/layout/PageContainer';
import PageField from '../../components/layout/PageField';
import { company, services } from '../../content/site';

const serviceText: Record<string, string> = {
  new: 'We design and build a product around a specific business problem, then leave code your team can maintain.',
  existing: 'We improve the software you already depend on, without a full rewrite.',
  ai: 'We put AI into a workflow people already use, with a person in the loop where a wrong answer is costly.',
  repair: 'We take an AI-built prototype that looks finished and make it software a business can run.',
};

const About = () => {
  return (
    <>
      <PageField>
        <PageContainer>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
            About
          </Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.25rem' }, maxWidth: 760, mt: 1 }}>
            {company.name}
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 680, fontSize: '1.125rem', lineHeight: 1.6, mt: 2 }}>
            Hochbegabt is German for “{company.meaning}.” We use the name to mean hardworking: stay with the problem until the software is clear, carefully made, and fit for the people who have to live with it.
          </Typography>
        </PageContainer>
      </PageField>
      <PageContainer>
        <Box component="section" sx={{ maxWidth: 720, mt: 1 }}>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' } }}>
            A business problem that needs software
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.7 }}>
            You might need a product that does not exist yet. You might depend on a system that is hard to change. You might want AI in a workflow people already use. Or you might have a prototype that looks finished and is not ready to run.
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.7 }}>
            We work with the people who own that problem.
          </Typography>
        </Box>

        <Box component="section" sx={{ maxWidth: 720, mt: 5 }}>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' } }}>
            What you can hire us to do
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.7 }}>
            Four services. We take the one that matches the problem.
          </Typography>
          {services.map((service) => (
            <Typography key={service.id} sx={{ mt: 2, lineHeight: 1.7 }}>
              <Box component="strong" sx={{ fontWeight: 700 }}>{service.title}.</Box>{' '}
              {serviceText[service.id]}
            </Typography>
          ))}
        </Box>

        <Box component="section" sx={{ maxWidth: 720, mt: 5 }}>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.6rem', md: '2rem' } }}>
            What you leave with
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.7 }}>
            The work ends when your team can run the software and change it. You keep the code. Your team makes the next change without us.
          </Typography>
          <Typography sx={{ mt: 1.5, lineHeight: 1.7 }}>
            You talk to the people who design and build the software. Tell us what it has to do. We will tell you whether we are the right firm.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
            <Button component={Link} to="/services" variant="outlined">
              See the services
            </Button>
            <Button component={Link} to="/contact" variant="contained">
              Talk about a project
            </Button>
          </Box>
        </Box>
      </PageContainer>
    </>
  );
};

export default About;
